import { logger } from '@belong/logging/logger';
import { composeAndRunSSPChain } from '@belong/ssp-execution';
import { PRODUCT_FAMILY } from '@belong/types/productFamily';
import { withBasePath, withoutParams } from '@belong/url-utils';
import { withServerSideAppProps } from 'src/helpers/nextjs/withServerSideAppProps';
import {
  adobeTargetSSP,
  alertDismissalSSP,
  fetchProductsAndOffers,
  getContentRetrievalSSP,
  promoCodeSSP,
  schemaDataSSP
} from '../../ssp-chain';
import { TPublicSSPContext } from '../../ssp-chain/types';
import type { IDynamicPageProps, IPageData, PageServerSidePropsFunction } from '../../core/types';

export { default } from '../[[...slug]]';

export const getPageServerSideProps: PageServerSidePropsFunction<IDynamicPageProps> = async (
  context,
  appServerSideProps
) => {
  const { correlationId } = appServerSideProps;
  const { resolvedUrl } = context;
  const pageUrl = withoutParams(resolvedUrl);
  logger.debug(correlationId, `Served by public: ${pageUrl}`);

  // Compose SSP context
  const initialCtx: TPublicSSPContext = {
    context,
    appServerSideProps,
    pageProps: {} as IPageData,
    pagePath: withBasePath(pageUrl)
  };

  // Compose and run SSP chain (status code, content, adobe target, blog/related articles, alert, schema data)
  // NOTE: Page Data is not available in the context until after the adobe target SSP process
  const finalCtx = await composeAndRunSSPChain<TPublicSSPContext>([
    getContentRetrievalSSP(),
    adobeTargetSSP,
    // Fetch mobile plans and offers
    fetchProductsAndOffers([PRODUCT_FAMILY.MOBILE_PLANS, true]),
    promoCodeSSP,
    alertDismissalSSP,
    schemaDataSSP
  ])(initialCtx);

  const { swrInitialCache, ...pageProps } = finalCtx.pageProps;

  return {
    props: {
      pageData: {
        ...pageProps,
        statusCode: finalCtx.statusCode,
        adobeTargetActivities: finalCtx.adobeTargetActivities,
        alert: finalCtx.alert,
        schemaData: finalCtx.schemaData
      },
      swrInitialCache
    }
  };
};

// Dedicated page entry point for mobile-specific server-side data loading.
export const getServerSideProps = withServerSideAppProps(getPageServerSideProps);
